import { getWebBuilderBrief, getWebBuilderBriefVariables } from '__generated__/getWebBuilderBrief';
import { WebBuilderBriefFields } from '__generated__/WebBuilderBriefFields';
import { Currency } from 'common/utils/currency';
import Brief from 'projects/routes/Brief';
import BriefQuery from 'projects/routes/BriefQuery';
import InviteDesigners from 'projects/routes/InviteDesignersPage';
import { Industries } from 'projects/util/industries';
import { BriefStep, StepState } from 'projects/util/step';
import React from 'react';
import { Route, Switch, useRouteMatch } from 'react-router';
import AboutYouPage from './AboutYouPage';
import DetailsPage from './DetailsPage';
import SummaryPage from './SummaryPage';
import TimeAndBudgetAndPage from './TimeAndBudgetPage';
import queries from './WebBuilderBrief.graphql';

interface RouteParams {
    briefId: string;
    step: string;
}

const steps: BriefStep<WebBuilderBriefFields>[] = [
    {
        step: 1,
        title: __('Tell us about your project'),
        fields: [
            'title',
            'description',
            'referenceFiles',
            'inspirationFiles',
            'inspirationUrls',
            'url',
            'assets',
        ],
    },
    {
        step: 2,
        title: __('Your time & budget'),
        fields: ['startDate', 'endDate', 'timeframe', 'budget'],
    },
    {
        step: 3,
        title: __('About you'),
        fields: ['industry', 'profileApproval', 'other'],
    },
    { step: 4, title: __('Review your brief') },
    {
        step: 5,
        title: __('Invite designers to quote'),
        isAuthRequired: true,
        isDesignerStep: true,
        nextActionLabel: __('Confirm designers'),
    },
];

export function WebBuilderBrief() {
    const route = useRouteMatch<RouteParams>();

    return (
        <BriefQuery<getWebBuilderBrief, getWebBuilderBriefVariables>
            query={queries.getWebBuilderBrief}
            variables={{ id: route!.params.briefId }}
            fieldsTypename="WebBuilderBriefFields"
        >
            {(data, refetch) => {
                const step = new StepState({
                    steps,
                    brief: data.brief,
                    currentStep: parseInt(route!.params.step, 10),
                });

                const brief = {
                    ...data.brief,
                    fields: data.brief.fields as WebBuilderBriefFields,
                };

                const industries = new Industries(data.industries);
                const siteCurrency = data.site.currency as Currency;

                return (
                    <Brief
                        refetch={refetch}
                        schema="webBuilder"
                        brief={brief}
                        viewer={data.viewer}
                        stepState={step}
                    >
                        {form => (
                            <Switch>
                                <Route
                                    path={`/briefs/web-builder/:briefId/1`}
                                    render={() => <DetailsPage brief={brief} form={form} />}
                                />
                                <Route
                                    path={`/briefs/web-builder/:briefId/2`}
                                    render={() => (
                                        <TimeAndBudgetAndPage
                                            brief={brief}
                                            form={form}
                                            siteCurrency={siteCurrency}
                                        />
                                    )}
                                />
                                <Route
                                    path={`/briefs/web-builder/:briefId/3`}
                                    render={() => (
                                        <AboutYouPage
                                            brief={brief}
                                            form={form}
                                            industries={data.industries}
                                        />
                                    )}
                                />
                                <Route
                                    path={`/briefs/web-builder/:briefId/4`}
                                    render={() => (
                                        <SummaryPage brief={brief} industries={industries} />
                                    )}
                                />
                                <Route
                                    path={`/briefs/web-builder/:briefId/5`}
                                    render={() => (
                                        <InviteDesigners brief={brief} viewer={data.viewer} />
                                    )}
                                />
                            </Switch>
                        )}
                    </Brief>
                );
            }}
        </BriefQuery>
    );
}
